import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { parseDecimal } from "@bw/shared/utils";
import { asMoney } from "@bw/shared/types";
import {
  updateSettings,
  type PlatformSettingsUpdate,
  type ValidatedPlatformSettings,
} from "../api";
import {
  reconcilePlatformSettings,
  SettingsAuthorityError,
  PLATFORM_SETTINGS_MUTATION_KEY,
} from "../settingsAuthority";
import {
  useSettingsAuthority,
  type SettingsDesiredMutation,
  type SettingsWriteLease,
} from "../settingsAuthorityStore";
import { settingsUpdateErrorMessage } from "../settingsErrors";

type FeeDraft = Record<string, string>;

function toDraft(
  fees: Record<string, string> | null | undefined,
): FeeDraft | null {
  if (!fees || Object.keys(fees).length === 0) return null;
  return Object.fromEntries(
    Object.entries(fees).map(([currency, fee]) => [currency, String(fee)]),
  );
}

function validCurrency(currency: string): boolean {
  return /^[a-z]{3}$/.test(currency);
}

function validFees(fees: FeeDraft): boolean {
  const entries = Object.entries(fees);
  return (
    entries.length > 0 &&
    entries.every(([currency, fee]) => {
      const amount = parseDecimal(fee);
      return validCurrency(currency) && amount !== null && amount > 0;
    })
  );
}

/** Platform-owned future campaign-fee defaults; campaign terms snapshot them. */
export function CampaignFeeSettingsPanel({
  settings,
}: {
  settings: ValidatedPlatformSettings;
}) {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  const serverFees = useMemo(
    () => toDraft(settings.default_campaign_fee_by_currency),
    [settings.default_campaign_fee_by_currency],
  );
  const [fees, setFees] = useState<FeeDraft>(() => serverFees ?? {});
  const [newCurrency, setNewCurrency] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const feeSignature = useMemo(
    () => JSON.stringify(serverFees ?? {}),
    [serverFees],
  );

  useEffect(() => {
    setFees(serverFees ?? {});
    setMessage(null);
    setError(null);
  }, [feeSignature, serverFees]);

  const valid = validFees(fees);
  const dirty = JSON.stringify(fees) !== JSON.stringify(serverFees ?? {});
  const mutation = useMutation({
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
    mutationFn: async ({
      lease,
      payload,
    }: {
      lease: SettingsWriteLease;
      payload: PlatformSettingsUpdate;
    }) => {
      await updateSettings(payload);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return reconcilePlatformSettings(queryClient, () =>
        authority.isWriteOperationCurrent(lease),
      );
    },
  });

  function setFee(currency: string, value: string) {
    setFees((previous) => ({ ...previous, [currency]: value }));
    setMessage(null);
    setError(null);
  }

  function addCurrency() {
    const currency = newCurrency.trim().toLowerCase();
    if (!validCurrency(currency) || fees[currency]) return;
    setFees((previous) => ({ ...previous, [currency]: "1.00" }));
    setNewCurrency("");
  }

  function removeCurrency(currency: string) {
    if (Object.keys(fees).length <= 1) return;
    setFees((previous) => {
      const next = { ...previous };
      delete next[currency];
      return next;
    });
    setMessage(null);
    setError(null);
  }

  async function save() {
    if (!valid || !dirty || mutation.isPending || !authority.tryStartWrite()) {
      return;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) {
      authority.finishWrite();
      return;
    }
    const desired = Object.freeze({
      kind: "campaign-fees",
      fees: Object.freeze({ ...fees }),
    }) satisfies SettingsDesiredMutation;
    try {
      const result = await mutation.mutateAsync({
        lease,
        payload: {
          default_campaign_fee_by_currency: Object.fromEntries(
            Object.entries(fees).map(([currency, fee]) => [
              currency,
              asMoney(fee)!,
            ]),
          ),
        },
      });
      if (!result || !authority.isWriteOperationCurrent(lease)) return;
      authority.recordResolution(desired, "confirmed");
      setMessage("Future campaign fee defaults saved.");
    } catch (reason) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      if (reason instanceof SettingsAuthorityError) {
        authority.recordRecovery(desired);
      } else {
        setError(
          settingsUpdateErrorMessage(
            reason,
            "Could not save campaign fee defaults.",
          ),
        );
      }
    } finally {
      authority.finishWrite(lease);
    }
  }

  if (!serverFees) {
    return (
      <Alert severity="warning" role="alert">
        Campaign fee defaults are unavailable. Refresh Platform Settings before
        creating a campaign.
      </Alert>
    );
  }

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Campaign fee defaults
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Applied only when a new campaign is created. Existing campaign
            contracts keep their original fee.
          </Typography>
        </Stack>

        <Stack spacing={1.25}>
          {Object.entries(fees).map(([currency, fee]) => (
            <Stack
              key={currency}
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <TextField
                label="Currency"
                value={currency.toUpperCase()}
                disabled
                slotProps={{
                  htmlInput: { "aria-label": `${currency} currency` },
                }}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                label="Fee per redeemed campaign booking"
                type="number"
                value={fee}
                error={parseDecimal(fee) === null || parseDecimal(fee)! <= 0}
                onChange={(event) => setFee(currency, event.target.value)}
                slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                sx={{ maxWidth: 320 }}
              />
              <IconButton
                aria-label={`Remove ${currency} campaign fee`}
                disabled={Object.keys(fees).length <= 1}
                onClick={() => removeCurrency(currency)}
              >
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            label="Add currency"
            value={newCurrency}
            onChange={(event) => setNewCurrency(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 3 } }}
            placeholder="CAD"
            sx={{ maxWidth: 180 }}
          />
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            disabled={!validCurrency(newCurrency.trim().toLowerCase())}
            onClick={addCurrency}
          >
            Add currency
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Button
            size="small"
            variant="contained"
            disabled={
              !valid || !dirty || mutation.isPending || authority.writing
            }
            onClick={() => void save()}
          >
            {mutation.isPending ? "Saving…" : "Save campaign fees"}
          </Button>
          {message ? (
            <Typography color="success.main">{message}</Typography>
          ) : null}
        </Stack>
        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>
    </Paper>
  );
}
